/**
 * Factor2 Custom JavaScript
 * Enhancements for the Factor2 Shopify theme
 */

/**
 * Sticky Header Custom Element
 * Handles sticky behavior and compact mode on scroll
 */
class StickyHeader extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = document.querySelector('.section-header');
    this.headerIsAlwaysSticky =
      this.getAttribute('data-sticky-type') === 'always' ||
      this.getAttribute('data-sticky-type') === 'reduce-logo-size';
    this.headerBounds = {};

    this.setHeaderHeight();

    window.matchMedia('(max-width: 990px)').addEventListener('change', this.setHeaderHeight.bind(this));

    if (this.headerIsAlwaysSticky) {
      this.header.classList.add('shopify-section-header-sticky');
    }

    this.currentScrollTop = 0;
    this.preventReveal = false;
    this.predictiveSearch = this.querySelector('predictive-search');

    this.onScrollHandler = this.onScroll.bind(this);
    this.hideHeaderOnScrollUp = () => (this.preventReveal = true);

    this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
    window.addEventListener('scroll', this.onScrollHandler, false);

    this.createObserver();
  }

  setHeaderHeight() {
    document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
  }

  disconnectedCallback() {
    this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
    window.removeEventListener('scroll', this.onScrollHandler);
  }

  createObserver() {
    let observer = new IntersectionObserver((entries, observer) => {
      this.headerBounds = entries[0].intersectionRect;
      observer.disconnect();
    });

    observer.observe(this.header);
  }

  onScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    if (this.predictiveSearch && this.predictiveSearch.isOpen) return;

    if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
      this.header.classList.add('scrolled-past-header');
      if (this.preventHide) return;
      requestAnimationFrame(this.hide.bind(this));
    } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
      this.header.classList.add('scrolled-past-header');
      if (!this.preventReveal) {
        requestAnimationFrame(this.reveal.bind(this));
      } else {
        window.clearTimeout(this.isScrolling);

        this.isScrolling = setTimeout(() => {
          this.preventReveal = false;
        }, 66);

        requestAnimationFrame(this.hide.bind(this));
      }
    } else if (scrollTop <= this.headerBounds.top) {
      this.header.classList.remove('scrolled-past-header');
      requestAnimationFrame(this.reset.bind(this));
    }

    this.currentScrollTop = scrollTop;
  }

  hide() {
    if (this.headerIsAlwaysSticky) return;
    this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
    this.closeMenuDisclosure();
    this.closeSearchModal();
    this.closeMegaMenus();
  }

  reveal() {
    if (this.headerIsAlwaysSticky) return;
    this.header.classList.add('shopify-section-header-sticky', 'animate');
    this.header.classList.remove('shopify-section-header-hidden');
  }

  reset() {
    if (this.headerIsAlwaysSticky) return;
    this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
  }

  closeMenuDisclosure() {
    this.disclosures = this.disclosures || this.header.querySelectorAll('header-menu');
    this.disclosures.forEach((disclosure) => disclosure.close());
  }

  closeSearchModal() {
    const detailsModal = this.header.querySelector('details-modal');
    if (detailsModal && typeof detailsModal.close === 'function') {
      detailsModal.close(false);
    }

    const predictiveSearch = this.header.querySelector('predictive-search');
    if (predictiveSearch && typeof predictiveSearch.close === 'function') {
      predictiveSearch.close(false);
    }
  }

  closeMegaMenus() {
    const megaMenus = this.header.querySelectorAll('factor2-mega-menu');
    megaMenus.forEach((menu) => menu.close());
  }
}

if (!customElements.get('sticky-header')) {
  customElements.define('sticky-header', StickyHeader);
}

/**
 * Factor2 Mega Menu Custom Element
 * Handles dropdown menus with hover and click support
 */
class Factor2MegaMenu extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('.factor2-mega-btn');
    this.panel = this.querySelector('.factor2-mega-menu__panel');
    this.isOpen = false;
    this.closeTimeout = null;
    this.openDelay = 50;
    this.closeDelay = 200;
    // Kept so the document-level listener can be removed again on disconnect;
    // .bind() returns a new function each call, which removeEventListener won't match.
    this.boundOutsideClick = this.handleOutsideClick.bind(this);
    this.panelGutter = 16;
    this.handleResize = this.setPanelMaxHeight.bind(this);
  }

  connectedCallback() {
    if (!this.button || !this.panel) return;

    this.addEventListener('mouseenter', this.handleMouseEnter.bind(this));
    this.addEventListener('mouseleave', this.handleMouseLeave.bind(this));

    this.button.addEventListener('click', this.handleClick.bind(this));

    this.button.addEventListener('keydown', this.handleKeydown.bind(this));
    this.panel.addEventListener('keydown', this.handlePanelKeydown.bind(this));

    document.addEventListener('click', this.boundOutsideClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.boundOutsideClick);
    window.removeEventListener('resize', this.handleResize);
  }

  setPanelMaxHeight() {
    const available = window.innerHeight - this.getBoundingClientRect().bottom - this.panelGutter;
    this.style.setProperty('--factor2-mega-max-height', `${Math.floor(available)}px`);
  }

  handleMouseEnter() {
    if (window.innerWidth < 990) return;
    clearTimeout(this.closeTimeout);
    setTimeout(() => this.open(), this.openDelay);
  }

  handleMouseLeave() {
    if (window.innerWidth < 990) return;
    this.closeTimeout = setTimeout(() => this.close(), this.closeDelay);
  }

  handleClick(e) {
    e.preventDefault();
    e.stopPropagation();
    this.toggle();
  }

  handleKeydown(e) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.toggle();
    } else if (e.key === 'Escape') {
      this.close();
      this.button.focus();
    } else if (e.key === 'ArrowDown' && this.isOpen) {
      e.preventDefault();
      const firstLink = this.panel.querySelector('a, button');
      if (firstLink) firstLink.focus();
    }
  }

  handlePanelKeydown(e) {
    if (e.key === 'Escape') {
      this.close();
      this.button.focus();
    }
  }

  handleOutsideClick(e) {
    if (!this.contains(e.target) && this.isOpen) {
      this.close();
    }
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    if (this.isOpen) return;

    document.querySelectorAll('factor2-mega-menu').forEach((menu) => {
      if (menu !== this) menu.close();
    });

    this.isOpen = true;
    this.setPanelMaxHeight();
    window.addEventListener('resize', this.handleResize);
    this.button.setAttribute('aria-expanded', 'true');
    this.panel.setAttribute('aria-hidden', 'false');
    this.classList.add('factor2-mega-menu--open');
  }

  close() {
    if (!this.isOpen) return;

    this.isOpen = false;
    window.removeEventListener('resize', this.handleResize);
    this.button.setAttribute('aria-expanded', 'false');
    this.panel.setAttribute('aria-hidden', 'true');
    this.classList.remove('factor2-mega-menu--open');
  }
}

if (!customElements.get('factor2-mega-menu')) {
  customElements.define('factor2-mega-menu', Factor2MegaMenu);
}

(function () {
  'use strict';

  /**
   * Sticky Header Shadow
   * Adds shadow to header when page is scrolled
   */
  function initStickyHeaderShadow() {
    const headerWrapper = document.querySelector('.header-wrapper');
    if (!headerWrapper) return;

    const scrollThreshold = 50;

    function updateHeaderShadow() {
      if (window.scrollY > scrollThreshold) {
        headerWrapper.classList.add('header-wrapper--scrolled');
      } else {
        headerWrapper.classList.remove('header-wrapper--scrolled');
      }
    }

    // Use passive listener for better scroll performance
    window.addEventListener('scroll', updateHeaderShadow, { passive: true });

    // Initial check
    updateHeaderShadow();
  }

  /**
   * Smooth scroll for anchor links
   */
  function initSmoothScroll() {
    document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
      anchor.addEventListener('click', function (e) {
        const targetId = this.getAttribute('href');
        if (targetId === '#') return;

        const targetElement = document.querySelector(targetId);
        if (targetElement) {
          e.preventDefault();
          targetElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      });
    });
  }

  /**
   * Button Wave Animation
   * Injects wave and bubble elements into buttons for liquid fill effect
   */
  function initButtonWaveAnimation() {
    const buttonSelectors = ['.button--primary', '.button--secondary', '.button--accent'];

    function addWaveElements(button) {
      if (button.querySelector('.wave')) return;

      const textContent = button.innerHTML;
      const hasSpan = button.querySelector('span');

      if (!hasSpan) {
        button.innerHTML = `<span>${textContent}</span>`;
      }

      const waveHTML = `
        <div class="wave">
          <div class="wave-1"></div>
          <div class="wave-2"></div>
          <div class="wave-3"></div>
        </div>
        <div class="bubble bubble-1"></div>
        <div class="bubble bubble-2"></div>
        <div class="bubble bubble-3"></div>
        <div class="bubble bubble-4"></div>
        <div class="bubble bubble-5"></div>
      `;

      button.insertAdjacentHTML('beforeend', waveHTML);
    }

    function processButtons() {
      buttonSelectors.forEach((selector) => {
        document.querySelectorAll(selector).forEach(addWaveElements);
      });
    }

    processButtons();

    const observer = new MutationObserver((mutations) => {
      let shouldProcess = false;
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length) {
          shouldProcess = true;
        }
      });
      if (shouldProcess) {
        processButtons();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Initialize all Factor2 enhancements
   */
  function init() {
    initStickyHeaderShadow();
    initSmoothScroll();
    initButtonWaveAnimation();
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
